import { Link } from 'react-router-dom';
import { Button, Card, CardContent, Badge } from '../components/ui';
import {
  ClockIcon,
  CurrencyDollarIcon,
  TrophyIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';

export default function HomePage() {
  const benefits = [
    {
      icon: ClockIcon,
      title: 'Save 2 Hours Per Bid',
      description: 'Instant fee calculations vs manual research on municipal websites.',
      color: 'text-primary',
    },
    {
      icon: CurrencyDollarIcon,
      title: 'Increase Bid Accuracy',
      description: 'Avoid $500+ permit fee estimation errors that hurt profitability.',
      color: 'text-success',
    },
    {
      icon: TrophyIcon,
      title: 'Win More Projects',
      description: 'Competitive & accurate bids help you win 30% more contracts.',
      color: 'text-orange',
    },
  ];

  const features = [
    'Instant permit fee calculations',
    'Coverage across 800+ municipalities',
    'Real-time fee updates',
    'Mobile-first design for field use',
    'Detailed fee breakdowns',
    'Historical search tracking',
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-gray-50 to-blue-50 py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Badge variant="primary" size="md" className="mb-6">
              Trusted by electrical contractors
            </Badge>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Calculate Permit Fees Instantly For Your Next Electrical Job
            </h1>
            <p className="text-lg md:text-xl text-gray-700 mb-8 max-w-2xl mx-auto">
              Get accurate permit fees in 30 seconds, not 2 hours. Stop wasting time on confusing municipal websites.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register">
                <Button variant="primary" size="lg" className="w-full sm:w-auto">
                  Start Free Trial
                </Button>
              </Link>
              <Button variant="secondary" size="lg" className="w-full sm:w-auto">
                Watch Demo (60 sec)
              </Button>
            </div>
            <p className="mt-4 text-sm text-gray-600">
              7-day free trial · No credit card required · Cancel anytime
            </p>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Electrical Contractors Choose PermitBuddy
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Save time, increase accuracy, and win more projects with instant permit fee calculations.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {benefits.map((benefit, index) => (
              <Card key={index} variant="elevated" padding="lg" className="text-center">
                <CardContent>
                  <div className={`mx-auto mb-4 w-12 h-12 ${benefit.color}`}>
                    <benefit.icon className="w-full h-full" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    {benefit.title}
                  </h3>
                  <p className="text-gray-600">
                    {benefit.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              How It Works
            </h2>
            <p className="text-lg text-gray-600">
              Three simple steps to accurate permit fees
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="text-center">
              <div className="w-16 h-16 bg-primary text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                1
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Enter City & Job Details
              </h3>
              <p className="text-gray-600">
                Tell us where you're working and what type of electrical job it is.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-teal text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                2
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                AI Finds Requirements
              </h3>
              <p className="text-gray-600">
                Our system discovers the permit portal and extracts fee structures.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-orange text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                3
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Get Instant Fee Breakdown
              </h3>
              <p className="text-gray-600">
                Receive detailed fee calculations with methodology and confidence scores.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Everything You Need
              </h2>
              <p className="text-lg text-gray-600">
                Built specifically for electrical contractors
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {features.map((feature, index) => (
                <div key={index} className="flex items-start gap-3">
                  <CheckCircleIcon className="w-6 h-6 text-success flex-shrink-0 mt-1" />
                  <span className="text-gray-700 text-lg">{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-primary text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Save Time on Permit Fees?
          </h2>
          <p className="text-lg md:text-xl mb-8 max-w-2xl mx-auto opacity-90">
            Join electrical contractors who trust PermitBuddy for accurate permit fee calculations.
          </p>
          <Link to="/register">
            <Button variant="orange" size="lg">
              Start Free Trial - No Credit Card Required
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
